import { zodResolver } from "@hookform/resolvers/zod";
import { Check, X } from "lucide-react";
import { type PropsWithChildren, useState } from "react";
import { useForm, useFormContext } from "react-hook-form";
import type { ZodSchema, z } from "zod";

import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTranslation } from "react-i18next";
import { LoadingSpinner } from "../ui/spinner";

interface PopoverFormProps {
  children: React.ReactNode;
}

const PopoverForm = ({ children }: PopoverFormProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {children}
    </Popover>
  );
};

const PopoverFormTrigger = PopoverTrigger;

type PopoverFormContentProps<T extends ZodSchema> = PropsWithChildren & {
  onSubmit: (values: z.infer<T>) => void | Promise<void>;
  initialValues?: z.infer<T>;
  schema?: T;
};

const PopoverFormContent = <T extends ZodSchema>({
  children,
  initialValues,
  schema,
  onSubmit,
}: PopoverFormContentProps<T>) => {
  const { t } = useTranslation();
  const form = useForm({
    values: initialValues,
    ...(schema && {
      resolver: zodResolver(schema),
    }),
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const _form = e.currentTarget.closest("form");
      _form?.querySelectorAll("input").forEach((input) => input.blur());
      _form?.dispatchEvent(
        new Event("submit", { cancelable: true, bubbles: true }),
      );
    }
  };

  return (
    <PopoverContent className="w-80" align="end">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} onKeyDown={handleKeyDown}>
          {
            <div className="grid gap-4">
              {children}
              <div className="flex justify-end gap-2">
                <PopoverClose asChild>
                  <Button variant="outline" size="sm" className="gap-1">
                    <X className="h-4 w-4" />
                    {t("cancel")}
                  </Button>
                </PopoverClose>
                <Button
                  size="sm"
                  type="submit"
                  className="gap-1"
                  disabled={
                    form.formState.isSubmitting || !form.formState.isValid
                  }
                >
                  {form.formState.isSubmitting ? (
                    <LoadingSpinner className="h-4 w-4" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                  {t("save")}
                </Button>
              </div>
            </div>
          }
        </form>
      </Form>
    </PopoverContent>
  );
};

export { PopoverForm, PopoverFormContent, PopoverFormTrigger };
